import React, { useState, useEffect } from "react";
import {
  handleRegister,
  handleRemoveRegistration,
} from "services/registrationService";
import { fetchUsers } from "services/userService";
import UserSearch from "./UserSearch";
import RegistrationConfirmationForm from "./RegistrationConfirmationForm";
import { canManage } from "services/tournamentService";

const TournamentRegistrationForm = ({
  tournament,
  user,
  registrations,
  prefetchedUsers = [],
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersFetched, setUsersFetched] = useState(false);

  const registrationCount = registrations?.length || 0;
  const isFull =
    tournament.registrationLimit &&
    registrationCount >= tournament.registrationLimit;
  const isRegistrationDisabled = isFull || tournament.registrationClosed;

  const userRegistration = registrations?.find(
    (reg) => reg.userId === user?.uid || reg.teamMateId === user?.uid
  );

  const isAdmin = canManage(user, tournament.locationId);

  // 1. Set users from prefetchedUsers if present
  useEffect(() => {
    if (isAdmin && prefetchedUsers.length > 0) {
      const filtered = prefetchedUsers.filter((u) => u.id !== user?.uid);
      setUsers(filtered);
      setUsersFetched(true);
    }
  }, [prefetchedUsers, isAdmin, user?.uid]);

  // 2. Fetch users on demand if needed
  const fetchUsersIfNeeded = async () => {
    if (!usersFetched) {
      const all = await fetchUsers();
      const filtered = all.filter((u) => u.id !== user?.uid);
      setUsers(filtered);
      setUsersFetched(true);
    }
  };

  const handleRegisterClick = () => {
    if (isRegistrationDisabled) return;
    setIsEditing(true);
    if (tournament.type === "Double Open" && !usersFetched) {
      fetchUsersIfNeeded();
    }
  };

  const handleUnregister = () => {
    if (!userRegistration) return;

    const confirmed = window.confirm("Voulez-vous vraiment vous désinscrire ?");
    if (!confirmed) return;

    handleRemoveRegistration(userRegistration.id);
  };

  const handleConfirm = async (finalName, teamMateId, partnerName) => {
    await handleRegister(
      tournament,
      registrations,
      user,
      finalName,
      teamMateId,
      partnerName
    );
    setIsEditing(false);
  };

  const handleUnregisterClick = () => {
    if (tournament.registrationClosed && !isAdmin) {
      alert(
        "Les inscriptions sont fermées. Veuillez contacter l’organisateur pour vous désinscrire."
      );
      return;
    }
    handleUnregister();
  };

  return (
    <div className="w-full mt-4">
      {user && !isEditing && (
        <>
          {userRegistration ? (
            <button
              onClick={handleUnregisterClick}
              className="px-2 py-1 bg-orange-600 hover:bg-orange-500 text-white font-semibold text-xl rounded-lg transition w-full md:w-auto"
            >
              Se désinscrire
            </button>
          ) : (
            <button
              onClick={handleRegisterClick}
              disabled={tournament.registrationClosed || isRegistrationDisabled}
              className={`px-2 py-1 text-white font-semibold text-xl rounded-lg transition w-full md:w-auto ${
                tournament.registrationClosed || isRegistrationDisabled
                  ? "bg-gray-600 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-500"
              }`}
            >
              {tournament.registrationClosed
                ? "Inscriptions fermées"
                : isFull
                ? "Complet"
                : "S'inscrire"}
            </button>
          )}
        </>
      )}

      {isEditing && (
        <div className="mt-4">
          <RegistrationConfirmationForm
            tournament={tournament}
            user={user}
            users={users}
            onConfirm={handleConfirm}
            onCancel={() => setIsEditing(false)}
          />
        </div>
      )}

      {isAdmin && (
        <div className="mt-2">
          <UserSearch
            tournament={tournament}
            registrations={registrations}
            users={prefetchedUsers}
          />
        </div>
      )}
    </div>
  );
};

export default TournamentRegistrationForm;
